import React, { useState } from 'react';
import Papa from 'papaparse';
import { downloadInvoiceJSON, downloadInvoiceCSV } from '../lib/invoiceExport';
import InvoicePrintView from '../shared/components/InvoicePrintView';

export default function SalesInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);

  function handleInvoiceCreated(invoice) {
    setInvoices([...invoices, invoice]);
    setShowUploadModal(false);
  }

  function handleDiscardInvoice(invoiceId) {
    if (confirm('Discard this invoice draft?')) {
      setInvoices(invoices.filter(inv => inv.invoice_id !== invoiceId));
    }
  }

  function handleInvoiceUpdated(updatedInvoice) {
    setInvoices(invoices.map(inv => 
      inv.invoice_id === updatedInvoice.invoice_id ? updatedInvoice : inv
    ));
  }

  return (
    <div className="min-h-screen surface p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="text-mist uppercase tracking-[.25em] text-xs mb-2">
          Sales Site • Invoice Management
        </div>
        <h1 className="text-ice text-3xl font-bold mb-2">
          Sales Invoices
        </h1>
        <p className="text-mist/80">
          Invoices created from sales transactions
        </p>
      </div>

      {/* Sales Terms Panel */}
      <div className="border border-brand/30 bg-brand/5 rounded-hard p-5 mb-6">
        <h3 className="text-brand font-semibold mb-3 text-lg">Sales Terms (Simple)</h3>
        <ul className="text-mist/90 text-sm space-y-2 leading-relaxed">
          <li>• <strong className="text-ice">Invoice (Sales Artifact):</strong> describes what was sold and why money moved</li>
          <li>• <strong className="text-ice">Payment Events:</strong> incoming payments + platform fees (facts from PayPal/eBay)</li>
          <li>• <strong className="text-ice">Shipping Events (SCM):</strong> shipping costs and tracking are operational artifacts</li>
          <li>• <strong className="text-ice">Approval Boundary:</strong> Sales approves invoice → Finance consumes it</li>
          <li>• <strong className="text-ice">Finance Translation:</strong> Finance creates journal entries & ledger postings from approved artifacts</li>
        </ul>
        <div className="mt-3 pt-3 border-t border-brand/20 text-xs text-mist/70 italic">
          Core Principle: Users act → Systems produce artifacts → Policies move artifacts → No direct cross-system mutation
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between mb-6">
        <div className="text-mist">
          {invoices.length} invoice{invoices.length !== 1 ? 's' : ''} created
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          className="bg-brand text-ink px-6 py-2 rounded-none font-semibold"
        >
          Create Invoice (CSV)
        </button>
      </div>

      {/* Invoice List */}
      {invoices.length === 0 ? (
        <div className="border border-edge rounded-hard bg-ink p-12 text-center">
          <div className="text-mist text-lg mb-2">No invoices created yet</div>
          <div className="text-mist/70 mb-6">
            Upload a CSV file to create your first sales invoice
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            className="chip px-6 py-2 rounded-none"
          >
            Create Invoice (CSV)
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {invoices.map(invoice => (
            <InvoiceCard
              key={invoice.invoice_id}
              invoice={invoice}
              onDiscard={handleDiscardInvoice}
              onEdit={handleInvoiceUpdated}
            />
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <CSVUploadModal
          onClose={() => setShowUploadModal(false)}
          onInvoiceCreated={handleInvoiceCreated}
        />
      )}
    </div>
  );
}

function InvoiceCard({ invoice, onDiscard, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPrintView, setShowPrintView] = useState(false);

  const totalLineItems = invoice.line_items.reduce((sum, item) => sum + item.amount, 0);
  const totalFees = invoice.fees.reduce((sum, fee) => sum + fee.amount, 0);
  const totalAmount = totalLineItems + totalFees + invoice.shipping;

  function handleEditComplete(updatedInvoice) {
    onEdit(updatedInvoice);
    setShowEditModal(false);
  }

  // Sales produces invoice artifacts. Printing exports a snapshot.
  // Finance consumes approved artifacts independently.
  function handlePrintInvoice(e) {
    e.stopPropagation();
    setShowPrintView(true);
    // Small delay to ensure render completes before print dialog
    setTimeout(() => {
      window.print();
      setShowPrintView(false);
    }, 100);
  }

  return (
    <div className="border border-edge rounded-hard bg-[#0b171d] overflow-hidden">
      {/* Header */}
      <div 
        className="p-6 cursor-pointer hover:bg-[#0e1d26] transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-ice text-xl font-semibold">{invoice.invoice_id}</h3>
              <span className="text-xs text-yellow-400 bg-yellow-400/10 px-2 py-1 rounded">
                DRAFT
              </span>
              <span className="text-xs text-mist/70 bg-[#0e1d26] px-2 py-1 rounded">
                {invoice.source}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-mist/70">Customer</div>
                <div className="text-ice">{invoice.customer}</div>
              </div>
              <div>
                <div className="text-mist/70">Platform</div>
                <div className="text-ice">{invoice.platform}</div>
              </div>
              <div>
                <div className="text-mist/70">Date</div>
                <div className="text-ice">{invoice.date}</div>
              </div>
            </div>
          </div>
          <div className="text-right ml-6">
            <div className="text-mist/70 text-sm mb-1">Total Amount</div>
            <div className="text-ice text-2xl font-bold">
              ${totalAmount.toFixed(2)}
            </div>
            <div className="text-mist/70 text-xs mt-1">
              {invoice.line_items.length} item{invoice.line_items.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        <div className="text-mist/70 text-xs mt-3">
          {expanded ? '▼ Click to collapse' : '▶ Click to expand details'}
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-edge bg-ink/50 p-6 space-y-6">
          {/* Company Header (On-Screen View) */}
          <div className="invoice-header flex justify-between items-start pb-6 mb-6 border-b-2 border-brand/50">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-ice mb-1">Spirit Technologies LLC</h2>
              <div className="text-lg text-brand mb-2">Sales Invoice</div>
              <div className="text-sm text-mist/70">
                support@spirittech.io | spirittech.io
              </div>
            </div>
            <div className="text-right">
              <div className="mb-2">
                <div className="text-xs text-mist/70">Invoice ID</div>
                <div className="text-lg font-bold text-ice">{invoice.invoice_id}</div>
              </div>
              <div className="mb-2">
                <div className="text-xs text-mist/70">Date</div>
                <div className="font-semibold text-ice">{invoice.date}</div>
              </div>
              <div className="mb-2">
                <div className="text-xs text-mist/70">Platform</div>
                <div className="font-semibold text-ice">{invoice.platform}</div>
              </div>
              {invoice.order_id && (
                <div>
                  <div className="text-xs text-mist/70">Order Reference</div>
                  <div className="font-semibold text-ice">{invoice.order_id}</div>
                </div>
              )}
            </div>
          </div>

          {/* Transaction Summary */}
          <div className="invoice-section">
            <h4 className="text-ice font-semibold mb-3 text-lg border-b border-edge/50 pb-2">
              Transaction Summary
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-mist/70 mb-1">Customer</div>
                <div className="text-ice font-semibold">{invoice.customer}</div>
              </div>
              <div>
                <div className="text-mist/70 mb-1">Source Platform</div>
                <div className="text-ice font-semibold">
                  {invoice.platform}
                  {invoice.source && invoice.source !== invoice.platform && (
                    <span className="text-mist/70 text-xs"> ({invoice.source})</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Line Items (What Was Sold) */}
          <div className="invoice-section">
            <h4 className="text-ice font-semibold mb-3 text-lg border-b border-edge/50 pb-2">
              Line Items
            </h4>
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-2 text-xs text-mist/70 font-semibold mb-2">
                <div className="col-span-6">Description</div>
                <div className="col-span-2 text-right">Qty</div>
                <div className="col-span-2 text-right">Unit Price</div>
                <div className="col-span-2 text-right">Line Total</div>
              </div>
              {invoice.line_items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 bg-[#0b171d] border border-edge/50 rounded-none p-3">
                  <div className="col-span-6 text-mist">{item.name || item.description}</div>
                  <div className="col-span-2 text-right text-ice">{item.quantity || 1}</div>
                  <div className="col-span-2 text-right text-ice">${item.amount.toFixed(2)}</div>
                  <div className="col-span-2 text-right text-ice font-semibold">
                    ${((item.quantity || 1) * item.amount).toFixed(2)}
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 border-t border-edge/50">
                <div className="text-mist font-medium">Subtotal</div>
                <div className="text-ice font-bold">${totalLineItems.toFixed(2)}</div>
              </div>
            </div>
          </div>

          {/* Platform Fees & Adjustments */}
          {invoice.fees.length > 0 && (
            <div className="invoice-section">
              <h4 className="text-ice font-semibold mb-3 text-lg border-b border-edge/50 pb-2">
                Platform Fees & Adjustments
              </h4>
              <div className="space-y-2">
                {invoice.fees.map((fee, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-[#0b171d] border border-edge/50 rounded-none p-3">
                    <div className="text-mist">{fee.type || fee.description || fee.name}</div>
                    <div className="text-red-400 font-semibold">${fee.amount.toFixed(2)}</div>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2 border-t border-edge/50">
                  <div className="text-mist font-medium">Total Fees</div>
                  <div className="text-red-400 font-bold">${totalFees.toFixed(2)}</div>
                </div>
              </div>
            </div>
          )}

          {/* Shipping & Fulfillment (SCM) */}
          {invoice.shipping > 0 && (
            <div className="invoice-section">
              <h4 className="text-ice font-semibold mb-3 text-lg border-b border-edge/50 pb-2">
                Shipping & Fulfillment (SCM)
              </h4>
              <div className="flex items-center justify-between bg-[#0b171d] border border-edge/50 rounded-none p-3">
                <div className="text-mist">
                  {invoice.shipping?.description || 'Shipping Cost'}
                </div>
                <div className="text-ice font-semibold">${invoice.shipping.toFixed(2)}</div>
              </div>
              <div className="text-xs text-mist/60 italic mt-2">
                Shipping costs are managed by the SCM system.
              </div>
            </div>
          )}

          {/* Sales Notes / Narrative */}
          {invoice.notes && (
            <div className="invoice-section">
              <h4 className="text-ice font-semibold mb-3 text-lg border-b border-edge/50 pb-2">
                Sales Notes / Narrative
              </h4>
              <div className="bg-[#0b171d] border border-edge/50 rounded-none p-3 text-mist text-sm whitespace-pre-wrap">
                {invoice.notes}
              </div>
            </div>
          )}

          {/* Invoice Totals */}
          <div className="invoice-totals border-t-2 border-brand/50 pt-4 mt-6">
            <div className="space-y-2 max-w-md ml-auto">
              <div className="flex justify-between items-center py-1">
                <div className="text-mist">Subtotal</div>
                <div className="text-ice font-semibold">${totalLineItems.toFixed(2)}</div>
              </div>
              {totalFees > 0 && (
                <div className="flex justify-between items-center py-1">
                  <div className="text-mist">Total Fees</div>
                  <div className="text-red-400 font-semibold">${totalFees.toFixed(2)}</div>
                </div>
              )}
              {invoice.shipping > 0 && (
                <div className="flex justify-between items-center py-1">
                  <div className="text-mist">Shipping</div>
                  <div className="text-ice font-semibold">${invoice.shipping.toFixed(2)}</div>
                </div>
              )}
              <div className="flex justify-between items-center py-3 border-t-2 border-edge mt-2">
                <div className="text-xl font-bold text-ice">Invoice Total</div>
                <div className="text-2xl font-bold text-brand">${totalAmount.toFixed(2)}</div>
              </div>
            </div>
          </div>

          {/* Download Actions */}
          <div className="download-actions pt-4 border-t border-edge no-print">
            <div className="flex items-center justify-between mb-3">
              <div className="text-mist/70 text-xs">
                📦 Exports create an artifact snapshot for review before submission to Finance
              </div>
            </div>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={(e) => { e.stopPropagation(); handlePrintInvoice(e); }}
                className="bg-purple-600 text-white hover:bg-purple-700 px-5 py-2 rounded-none font-medium text-sm"
                title="Print invoice (opens print dialog)"
              >
                🖨️ Print Invoice
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); downloadInvoiceJSON(invoice); }}
                className="bg-emerald-600 text-white hover:bg-emerald-700 px-5 py-2 rounded-none font-medium text-sm"
                title="Download JSON (source-of-truth artifact)"
              >
                Download JSON
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); downloadInvoiceCSV(invoice); }}
                className="bg-blue-600 text-white hover:bg-blue-700 px-5 py-2 rounded-none font-medium text-sm"
                title="Download CSV (human/import-friendly)"
              >
                Download CSV
              </button>
              <button
                disabled
                className="bg-muted text-mist/50 px-5 py-2 rounded-none font-medium text-sm cursor-not-allowed"
                title="PDF export coming soon"
              >
                Download PDF (Soon)
              </button>
            </div>
          </div>

          {/* Edit & Discard Actions */}
          <div className="edit-actions flex gap-3 pt-4 border-t border-edge mt-4 no-print">
            {invoice.status === 'Draft' && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowEditModal(true); }}
                className="bg-brand text-ink hover:opacity-90 px-6 py-2 rounded-none font-semibold"
              >
                Edit Invoice
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onDiscard(invoice.invoice_id); }}
              className="border border-red-400 text-red-400 hover:bg-red-400/10 px-6 py-2 rounded-none"
            >
              Discard Draft
            </button>
            <div className="text-mist/70 py-2 text-sm">
              (Future: Submit to Finance workflow)
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <EditInvoiceModal
          invoice={invoice}
          onClose={() => setShowEditModal(false)}
          onSave={handleEditComplete}
        />
      )}

      {/* Print View (hidden on screen, visible only during print) */}
      {showPrintView && (
        <div className="fixed inset-0 bg-white z-50 overflow-auto">
          <InvoicePrintView invoice={invoice} />
        </div>
      )}
    </div>
  );
}

function CSVUploadModal({ onClose, onInvoiceCreated }) {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState(null);
  const [previewInvoice, setPreviewInvoice] = useState(null);

  function handleFileSelect(e) {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setPreviewInvoice(null);
    }
  }

  function parseCSV() {
    if (!file) return;

    setParsing(true);
    setError(null);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        try {
          const invoice = buildInvoiceFromCSV(result.data);
          setPreviewInvoice(invoice);
          setParsing(false);
        } catch (err) {
          setError(err.message);
          setParsing(false);
        }
      },
      error: (err) => {
        setError(`CSV parsing failed: ${err.message}`);
        setParsing(false);
      }
    });
  }

  function buildInvoiceFromCSV(rows) {
    if (!rows || rows.length === 0) {
      throw new Error('CSV file is empty');
    }

    // Expected headers
    const requiredHeaders = ['invoice_id', 'customer_name', 'date'];
    const firstRow = rows[0];
    const missing = requiredHeaders.filter(h => !(h in firstRow));
    if (missing.length > 0) {
      throw new Error(`Missing required columns: ${missing.join(', ')}`);
    }

    // Group by invoice_id (support for multi-row invoices)
    const invoiceId = rows[0].invoice_id;
    const invoiceRows = rows.filter(r => r.invoice_id === invoiceId);

    // Build line items
    const lineItems = [];
    const fees = [];
    let shipping = 0;
    let notes = '';

    invoiceRows.forEach(row => {
      if (row.line_item_name && row.line_item_amount) {
        lineItems.push({
          name: row.line_item_name,
          amount: parseFloat(row.line_item_amount) || 0
        });
      }

      if (row.fee_type && row.fee_amount) {
        fees.push({
          type: row.fee_type,
          amount: parseFloat(row.fee_amount) || 0
        });
      }

      if (row.shipping_amount) {
        shipping = Math.max(shipping, parseFloat(row.shipping_amount) || 0);
      }

      if (row.notes && !notes) {
        notes = row.notes;
      }
    });

    return {
      invoice_id: invoiceId,
      order_id: rows[0].order_id || '',
      customer: rows[0].customer_name,
      platform: rows[0].platform || 'Unknown',
      date: rows[0].date,
      line_items: lineItems,
      fees: fees,
      shipping: shipping,
      notes: notes,
      source: 'CSV Import',
      status: 'Draft',
      created_at: new Date().toISOString()
    };
  }

  function handleSaveDraft() {
    if (previewInvoice) {
      onInvoiceCreated(previewInvoice);
    }
  }

  function downloadTemplate() {
    const template = `invoice_id,order_id,customer_name,platform,date,line_item_name,line_item_amount,fee_type,fee_amount,shipping_amount,notes
INV-001,ORD-12345,John Doe,eBay,2026-01-02,Desktop PC,500.00,PayPal Fee,15.00,25.00,First sale
INV-001,ORD-12345,John Doe,eBay,2026-01-02,Monitor,300.00,,,,"Same invoice, multiple items"`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'invoice_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[#0b171d] border border-edge rounded-hard p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-ice text-2xl font-bold mb-4">Create Invoice from CSV</h2>

        {!previewInvoice ? (
          <>
            {/* Upload Section */}
            <div className="mb-6">
              <label className="block text-mist text-sm mb-2">Select CSV File</label>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="w-full bg-ink text-ice border border-edge rounded-none px-4 py-2"
              />
            </div>

            {/* Template Download */}
            <div className="border border-edge rounded-hard bg-ink/50 p-4 mb-6">
              <h3 className="text-ice font-semibold mb-2">CSV Format</h3>
              <p className="text-mist/80 text-sm mb-3">
                Required columns: invoice_id, customer_name, date
              </p>
              <p className="text-mist/80 text-sm mb-3">
                Optional columns: order_id, platform, line_item_name, line_item_amount, 
                fee_type, fee_amount, shipping_amount, notes
              </p>
              <button
                onClick={downloadTemplate}
                className="text-brand hover:text-brand/80 text-sm"
              >
                📥 Download CSV Template
              </button>
            </div>

            {/* Error Display */}
            {error && (
              <div className="border border-red-400 bg-red-900/20 rounded-hard p-4 mb-4">
                <div className="text-red-400 font-semibold mb-1">Error</div>
                <div className="text-mist text-sm">{error}</div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={parseCSV}
                disabled={!file || parsing}
                className="flex-1 bg-brand text-ink px-6 py-3 rounded-none font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {parsing ? 'Parsing...' : 'Parse CSV'}
              </button>
              <button
                onClick={onClose}
                className="chip px-6 py-3 rounded-none"
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Preview Section */}
            <div className="mb-6">
              <h3 className="text-ice font-semibold mb-4">Invoice Preview (Read-Only)</h3>
              
              <div className="space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-mist/70 mb-1">Invoice ID</div>
                    <div className="text-ice font-semibold">{previewInvoice.invoice_id}</div>
                  </div>
                  <div>
                    <div className="text-mist/70 mb-1">Customer</div>
                    <div className="text-ice">{previewInvoice.customer}</div>
                  </div>
                  <div>
                    <div className="text-mist/70 mb-1">Platform</div>
                    <div className="text-ice">{previewInvoice.platform}</div>
                  </div>
                  <div>
                    <div className="text-mist/70 mb-1">Date</div>
                    <div className="text-ice">{previewInvoice.date}</div>
                  </div>
                </div>

                {/* Line Items */}
                {previewInvoice.line_items.length > 0 && (
                  <div>
                    <div className="text-mist font-semibold mb-2">Line Items</div>
                    {previewInvoice.line_items.map((item, idx) => (
                      <div key={idx} className="flex justify-between bg-ink border border-edge/50 rounded-none p-2 mb-1">
                        <span className="text-mist text-sm">{item.name}</span>
                        <span className="text-ice font-semibold text-sm">${item.amount.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Fees */}
                {previewInvoice.fees.length > 0 && (
                  <div>
                    <div className="text-mist font-semibold mb-2">Fees</div>
                    {previewInvoice.fees.map((fee, idx) => (
                      <div key={idx} className="flex justify-between bg-ink border border-edge/50 rounded-none p-2 mb-1">
                        <span className="text-mist text-sm">{fee.type}</span>
                        <span className="text-red-400 text-sm">${fee.amount.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Shipping */}
                {previewInvoice.shipping > 0 && (
                  <div className="flex justify-between bg-ink border border-edge rounded-none p-2">
                    <span className="text-mist text-sm">Shipping</span>
                    <span className="text-ice font-semibold text-sm">${previewInvoice.shipping.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleSaveDraft}
                className="flex-1 bg-brand text-ink px-6 py-3 rounded-none font-semibold"
              >
                Save Draft (Local Only)
              </button>
              <button
                onClick={() => setPreviewInvoice(null)}
                className="chip px-6 py-3 rounded-none"
              >
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function EditInvoiceModal({ invoice, onClose, onSave }) {
  const [editedInvoice, setEditedInvoice] = useState({
    ...invoice,
    line_items: [...invoice.line_items],
    fees: [...invoice.fees]
  });

  // Live totals calculation
  const totalLineItems = editedInvoice.line_items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  const totalFees = editedInvoice.fees.reduce((sum, fee) => sum + (parseFloat(fee.amount) || 0), 0);
  const totalAmount = totalLineItems + totalFees + (parseFloat(editedInvoice.shipping) || 0);

  function updateField(field, value) {
    setEditedInvoice({ ...editedInvoice, [field]: value });
  }

  function updateLineItem(index, field, value) {
    const updated = [...editedInvoice.line_items];
    updated[index] = { ...updated[index], [field]: value };
    setEditedInvoice({ ...editedInvoice, line_items: updated });
  }

  function addLineItem() {
    setEditedInvoice({
      ...editedInvoice,
      line_items: [...editedInvoice.line_items, { name: '', amount: 0 }]
    });
  }

  function removeLineItem(index) {
    setEditedInvoice({
      ...editedInvoice,
      line_items: editedInvoice.line_items.filter((_, i) => i !== index)
    });
  }

  function updateFee(index, field, value) {
    const updated = [...editedInvoice.fees];
    updated[index] = { ...updated[index], [field]: value };
    setEditedInvoice({ ...editedInvoice, fees: updated });
  }

  function addFee() {
    setEditedInvoice({
      ...editedInvoice,
      fees: [...editedInvoice.fees, { type: '', amount: 0 }]
    });
  }

  function removeFee(index) {
    setEditedInvoice({
      ...editedInvoice,
      fees: editedInvoice.fees.filter((_, i) => i !== index)
    });
  }

  function handleSave() {
    onSave(editedInvoice);
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className="bg-[#0b171d] border border-edge rounded-hard p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-ice text-2xl font-bold mb-2">Edit Invoice</h2>
        <p className="text-yellow-400/80 text-sm mb-6">
          ⚠️ Edits are tracked. Invoice remains a draft until submitted.
        </p>

        <div className="space-y-6">
          {/* A. Invoice Metadata */}
          <div>
            <h3 className="text-ice font-semibold mb-3 text-lg">Invoice Metadata</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-mist text-sm mb-1">Invoice ID (read-only)</label>
                <input
                  type="text"
                  value={editedInvoice.invoice_id}
                  disabled
                  className="w-full bg-ink/50 text-mist/50 border border-edge/50 rounded-none px-3 py-2 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-mist text-sm mb-1">Order Reference (read-only)</label>
                <input
                  type="text"
                  value={editedInvoice.order_id || ''}
                  disabled
                  className="w-full bg-ink/50 text-mist/50 border border-edge/50 rounded-none px-3 py-2 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-mist text-sm mb-1">Customer Name</label>
                <input
                  type="text"
                  value={editedInvoice.customer}
                  onChange={(e) => updateField('customer', e.target.value)}
                  className="w-full bg-ink text-ice border border-edge rounded-none px-3 py-2 focus:border-brand focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-mist text-sm mb-1">Platform</label>
                <input
                  type="text"
                  value={editedInvoice.platform}
                  onChange={(e) => updateField('platform', e.target.value)}
                  className="w-full bg-ink text-ice border border-edge rounded-none px-3 py-2 focus:border-brand focus:outline-none"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-mist text-sm mb-1">Invoice Date</label>
                <input
                  type="text"
                  value={editedInvoice.date}
                  onChange={(e) => updateField('date', e.target.value)}
                  className="w-full bg-ink text-ice border border-edge rounded-none px-3 py-2 focus:border-brand focus:outline-none"
                  placeholder="YYYY-MM-DD"
                />
              </div>
            </div>
          </div>

          {/* B. Line Items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-ice font-semibold text-lg">Line Items</h3>
              <button
                onClick={addLineItem}
                className="text-brand hover:text-brand/80 text-sm font-semibold"
              >
                + Add Line Item
              </button>
            </div>
            <div className="space-y-2">
              {editedInvoice.line_items.map((item, idx) => (
                <div key={idx} className="flex gap-2 items-start bg-ink border border-edge/50 rounded-none p-3">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={item.name}
                      onChange={(e) => updateLineItem(idx, 'name', e.target.value)}
                      placeholder="Item name"
                      className="w-full bg-[#0b171d] text-ice border border-edge/30 rounded-none px-3 py-2 text-sm focus:border-brand focus:outline-none"
                    />
                  </div>
                  <div className="w-32">
                    <input
                      type="number"
                      value={item.amount}
                      onChange={(e) => updateLineItem(idx, 'amount', parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      step="0.01"
                      className="w-full bg-[#0b171d] text-ice border border-edge/30 rounded-none px-3 py-2 text-sm focus:border-brand focus:outline-none"
                    />
                  </div>
                  <button
                    onClick={() => removeLineItem(idx)}
                    className="text-red-400 hover:text-red-300 px-2 py-2 text-sm"
                    title="Remove"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <div className="flex justify-between pt-2 border-t border-edge/50 px-3">
                <span className="text-mist font-medium">Subtotal</span>
                <span className="text-ice font-bold">${totalLineItems.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* C. Fees */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-ice font-semibold text-lg">Fees</h3>
              <button
                onClick={addFee}
                className="text-brand hover:text-brand/80 text-sm font-semibold"
              >
                + Add Fee
              </button>
            </div>
            <div className="space-y-2">
              {editedInvoice.fees.map((fee, idx) => (
                <div key={idx} className="flex gap-2 items-start bg-ink border border-edge/50 rounded-none p-3">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={fee.type}
                      onChange={(e) => updateFee(idx, 'type', e.target.value)}
                      placeholder="Fee type"
                      className="w-full bg-[#0b171d] text-ice border border-edge/30 rounded-none px-3 py-2 text-sm focus:border-brand focus:outline-none"
                    />
                  </div>
                  <div className="w-32">
                    <input
                      type="number"
                      value={fee.amount}
                      onChange={(e) => updateFee(idx, 'amount', parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      step="0.01"
                      className="w-full bg-[#0b171d] text-ice border border-edge/30 rounded-none px-3 py-2 text-sm focus:border-brand focus:outline-none"
                    />
                  </div>
                  <button
                    onClick={() => removeFee(idx)}
                    className="text-red-400 hover:text-red-300 px-2 py-2 text-sm"
                    title="Remove"
                  >
                    ✕
                  </button>
                </div>
              ))}
              {editedInvoice.fees.length > 0 && (
                <div className="flex justify-between pt-2 border-t border-edge/50 px-3">
                  <span className="text-mist font-medium">Total Fees</span>
                  <span className="text-red-400 font-bold">${totalFees.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>

          {/* D. Shipping */}
          <div>
            <h3 className="text-ice font-semibold mb-3 text-lg">Shipping</h3>
            <div className="space-y-2">
              <div>
                <label className="block text-mist text-sm mb-1">Shipping Description</label>
                <input
                  type="text"
                  value={editedInvoice.shipping_description || 'Shipping Cost'}
                  onChange={(e) => updateField('shipping_description', e.target.value)}
                  className="w-full bg-ink text-ice border border-edge rounded-none px-3 py-2 focus:border-brand focus:outline-none"
                  placeholder="Shipping Cost"
                />
              </div>
              <div>
                <label className="block text-mist text-sm mb-1">Shipping Amount</label>
                <input
                  type="number"
                  value={editedInvoice.shipping}
                  onChange={(e) => updateField('shipping', parseFloat(e.target.value) || 0)}
                  step="0.01"
                  className="w-full bg-ink text-ice border border-edge rounded-none px-3 py-2 focus:border-brand focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* E. Notes */}
          <div>
            <h3 className="text-ice font-semibold mb-3 text-lg">Notes</h3>
            <textarea
              value={editedInvoice.notes || ''}
              onChange={(e) => updateField('notes', e.target.value)}
              rows={4}
              className="w-full bg-ink text-ice border border-edge rounded-none px-3 py-2 focus:border-brand focus:outline-none resize-none"
              placeholder="Additional notes about this invoice..."
            />
          </div>

          {/* Live Total Preview */}
          <div className="border border-brand/30 bg-brand/5 rounded-hard p-4">
            <div className="flex justify-between items-center">
              <span className="text-ice font-semibold text-lg">Total Amount</span>
              <span className="text-brand text-2xl font-bold">${totalAmount.toFixed(2)}</span>
            </div>
            <div className="text-mist/70 text-xs mt-1">
              Live calculation: {editedInvoice.line_items.length} items + {editedInvoice.fees.length} fees + shipping
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6 pt-6 border-t border-edge">
          <button
            onClick={handleSave}
            className="flex-1 bg-brand text-ink px-6 py-3 rounded-none font-semibold hover:opacity-90"
          >
            Save Changes
          </button>
          <button
            onClick={onClose}
            className="chip px-6 py-3 rounded-none"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
